import { ChangeEvent, ReactNode } from 'react';
import {
  ModuleTopBar,
  Box,
  Flex,
  Grid,
  Text,
  Select,
  VStack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import useStartYellowhiveOfferFlow from '@flows/src/hooks/flows-configure/useStartYellowhiveOfferFlow';
import { useQueryClient } from 'react-query';
import YellowhiveFlowProductsSidebar from '@flows/src/components/flow-products/YellowhiveFlowProductsSidebar';
import YellowhiveFlowProductsBottomBar from '@flows/src/components/flow-products/YellowhiveFlowProductsBottomBar';
import { useFlowInfo } from '@flows/src/api/getFlowInfo';
import { useYellowhiveQuotesWithPolling } from '@flows/src/hooks/flow-products/useYellowhiveQuotesWithPolling';
import { useFlowProductsOverviewParams } from '@flows/src/hooks/flow-products/useFlowProductsOverviewParams';
import { FLOWS_PRODUCTS_PATH } from '@flows/src/FlowsProductsRouter';
import useYellowhiveFlowProductSelectionStore from '@flows/src/store/zustand/flows-products/useYellowhiveFlowProductSelectionStore';

const YellowhiveFlowProductsLayout = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const sessionId = queryParams.get('session_id') || undefined;
  const { data: session } = useFlowsSession(sessionId);
  const history = useHistory();
  const { flowId } = useParams<{ flowId: string }>();
  const queryClient = useQueryClient();
  const { data: flowInfo } = useFlowInfo(flowId || '');
  const startYellowhiveOfferFlow = useStartYellowhiveOfferFlow();
  const { tagId } = useFlowProductsOverviewParams();
  const resetYellowhiveSelectionStore = useYellowhiveFlowProductSelectionStore(
    (state) => state.resetYellowhive,
  );
  const { data: yellowhiveQuotesData } = useYellowhiveQuotesWithPolling({
    sessionId,
    enabled: !!sessionId && flowInfo?.sub_type === 'YELLOWHIVE',
  });

  // Use navigation map for categories
  const categories = yellowhiveQuotesData?.navigationMap ?? [];

  // Handle category change for mobile
  const handleCategoryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newCategoryId = event.target.value;
    if (newCategoryId && flowId) {
      history.push({
        pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/overview/${newCategoryId}`,
        search: window.location.search,
      });
    }
  };

  const getBackText = () => {
    if (window.location.pathname.includes('selection-overview')) {
      return t('BACK-TO-DASHBOARD');
    }
    if (window.location.pathname.includes('basket')) {
      return t('BACK-TO-OVERVIEW');
    }
    return t('Back to questions');
  };
  const handlePrevious = () => {
    if (window.location.pathname.includes('basket')) {
      // clear selected yellowhive products
      resetYellowhiveSelectionStore();
      // OK way to preserve url state params
      return history.goBack();
    }
    if (flowId && sessionId) {
      // Clear the flow-products cache when navigating back to questionnaire
      queryClient.removeQueries(['flow-products', 'yellowhive', 'quotes']);

      startYellowhiveOfferFlow({
        flowId: flowId,
        uuid: sessionId,
        partyId: session?.prospect?.id,
        yellowhive_products: session?.yellowhive_products || [],
      });
    }
  };

  const getTopBarText = (): string => {
    if (window.location.pathname.includes('selection-overview')) {
      return t('FLOWS_PRODUCTS_SELECTION-OVERVIEW_TOP-BAR_TITLE');
    }
    return t('FLOWS_PRODUCTS_SELECTION-OVERVIEW_TOP-BAR_TITLE-YELLOWHIVE');
  };

  return (
    <Grid
      templateColumns={{
        base: '1fr', // Mobile: single column
        tablet: 'auto minmax(0, 1fr)', // Tablet+: sidebar + main content
      }}
      w="100%"
      h="100%"
      position="relative"
      minH="0"
    >
      {/* Sidebar - hidden on mobile */}
      <Box display={{ base: 'none', tablet: 'block' }}>
        <YellowhiveFlowProductsSidebar />
      </Box>

      <Grid templateRows="auto 1fr auto" position="relative" w="100%" minW="0">
        {/* Mobile Header */}
        <Box
          display={{ base: 'block', tablet: 'none' }}
          position="sticky"
          top="0"
          bg="white"
          zIndex="2"
          borderBottom="1px"
          borderColor="gray.50"
        >
          <VStack spacing="0" align="stretch">
            {/* Title */}
            <Flex align="center" justify="center" px="4" py="3" h="60px">
              <Text
                textStyle="h6Headline"
                textAlign="center"
                fontSize="md"
                fontWeight="600"
                color="gray.800"
                noOfLines={1}
              >
                {getTopBarText()}
              </Text>
            </Flex>

            {/* Category Selector for Yellowhive - only show on overview pages */}
            {flowInfo?.sub_type === 'YELLOWHIVE' &&
              categories.length > 1 &&
              !window.location.pathname.includes('basket') &&
              !window.location.pathname.includes('selection-overview') && (
                <Box
                  px="4"
                  pb="3"
                  pt="1"
                  borderTop="1px"
                  borderColor="gray.100"
                >
                  <Text fontSize="sm" color="gray.500" mb="2" fontWeight="500">
                    {t('Category')}:
                  </Text>
                  <Select
                    value={tagId || ''}
                    onChange={handleCategoryChange}
                    bg="white"
                    borderColor="gray.200"
                    borderRadius="lg"
                    h="44px"
                    fontSize="md"
                    fontWeight="500"
                    _focus={{
                      borderColor: 'primary.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)',
                    }}
                    _hover={{
                      borderColor: 'gray.300',
                    }}
                  >
                    {categories.map((category) => (
                      <option key={category.key} value={category.key}>
                        {category.label}
                      </option>
                    ))}
                  </Select>
                </Box>
              )}
          </VStack>
        </Box>

        {/* Desktop Header */}
        <Box
          display={{ base: 'none', tablet: 'block' }}
          position="sticky"
          top="0"
          bg="white"
          zIndex="1"
        >
          <ModuleTopBar title={getTopBarText()} />
        </Box>

        <Flex
          flexDirection="column"
          align="center"
          flexGrow="1"
          bg="gray.ultraLight"
          overflow="auto"
          minW="0"
        >
          {children}
        </Flex>
        {flowInfo?.sub_type === 'YELLOWHIVE' && (
          <YellowhiveFlowProductsBottomBar
            handlePrevious={handlePrevious}
            backText={getBackText()}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default YellowhiveFlowProductsLayout;
